import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getIngredientTypes,
  getLiquidClasses,
  getVendors,
  saveIngredient,
} from 'business/services/IngredientService';
import {
  useCurrentIngredientDetails,
  useHasBufferType,
  useHasNonBufferType,
  useIngredientDeletedData,
  useIngredientMergeAndDeletedStockIds,
  useIngredientStocks,
  useIngredientTypes,
  useIsDirty,
  useOriginalIngredientDetails,
} from 'redux/hooks/IngredientDetail';
import { UseGetHeaderButtonsProps, UseGetStocksTableDataProps } from './utils/Constants';
import {
  IIngredientTypeDTO,
  IntPropertyDTO,
  ResponseType,
  StringPropertyDTO,
} from 'business/models/Models';
import { StockRowRenderer } from './StockRowRenderer';
import { useRMDispatch } from 'redux/hooks';
import {
  discardChanges,
  getIngredientData,
  setAliasErrors,
  setBufferDataErrors,
  setCasNumberErrors,
  setDirty,
  setErrorVisibility,
  setLoadingState,
  setStockErrors,
} from 'redux/reducers/ingredientDetailReducer';
import { GlobalPreferencesService } from 'business/services/GlobalPreferencesService';
import { PropertyConstants } from 'enums/PropertyConstants';
import { IOption } from 'components/input/selectInput';
import { getIngredientDocModelObject } from 'transformers/IngredientDetails';
import { setIngTableDataIndicator } from 'redux/reducers/ingredientTableReducer';
import { useIngredientsDataIndicator } from 'redux/hooks/ingredients';
import { validateIngredient } from './utils/utils';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import {
  useSaveDiscardButtons,
  UseSaveDiscardButtonsParams,
} from 'CustomHooks/useSaveDiscardButtons';

export const useGetAllNewTypes = () => {
  const types = useIngredientTypes();
  const [allIngTypes, setAllIngTypes] = useState<IIngredientTypeDTO[]>([]);

  useEffect(() => {
    getIngredientTypes().then((ings) => {
      setAllIngTypes(ings);
    });
  }, []);

  const allIngTypeRows: IIngredientTypeDTO[] = useMemo(
    () =>
      allIngTypes
        .filter((i) => !types.find((t) => i.id === t.id))
        .map((ing) => {
          return { id: ing.id, name: ing.name, isBuffer: ing.isBuffer };
        }),
    [allIngTypes, types],
  );

  return allIngTypeRows;
};

export const useGetHeaderButtons = ({
  setError,
  ingredientInfo,
  closeModal,
}: UseGetHeaderButtonsProps) => {
  const stocks = useIngredientStocks();
  const dispatch = useRMDispatch();
  const ingredientDetail = useCurrentIngredientDetails();
  const originalData = useOriginalIngredientDetails();
  const deletedData = useIngredientDeletedData();
  const ingTableDataIndicator = useIngredientsDataIndicator();
  const isDirty = useIsDirty();
  const mergedAndDeletedStockIds = useIngredientMergeAndDeletedStockIds();

  const onSaveButtonClick = useCallback(async () => {
    const error = validateIngredient(ingredientDetail);
    if (error.msg !== '') {
      setError({ show: true, msg: error.msg });
      dispatch(setStockErrors(error.stockRowInfo));
      dispatch(setAliasErrors(error.aliasRowInfo));
      dispatch(setCasNumberErrors(error.casNumberRowInfo));
      dispatch(setBufferDataErrors(error.bufferDataErrors));
      dispatch(setErrorVisibility(true));
    } else {
      dispatch(setLoadingState(true));
      const saveData = getIngredientDocModelObject(ingredientDetail, deletedData);
      saveData.ingredient.types = saveData.ingredient.types.filter(
        (t) => !originalData.types.find((type) => type.id === t.id),
      );
      saveData.deletedData.types = saveData.deletedData.types.filter(
        (tId) => !saveData.ingredient.types.find((type) => type.id === tId),
      );
      saveData.mergedAndDeletedStockIds = mergedAndDeletedStockIds;

      saveIngredient(saveData)
        .then((response) => {
          if (response.responseType === ResponseType.Success) {
            dispatch(getIngredientData(response.value));
            dispatch(setIngTableDataIndicator(ingTableDataIndicator + 1));
            dispatch(setDirty(false));
            showAlertSnackBar(
              EAlertType.SUCCESS,
              ESnackBarPosition.TOP,
              response.message,
            );
            closeModal ? closeModal() : null;
          } else {
            showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, response.message);
          }
          dispatch(setLoadingState(false));
        })
        .catch((e) => {
          dispatch(setLoadingState(false));
          throw e;
        });
    }
  }, [stocks, ingredientDetail, deletedData]);

  const onDiscardButtonClick = () => {
    dispatch(discardChanges());
  };

  const saveDiscardButtonsParams: UseSaveDiscardButtonsParams = {
    enableButtons: isDirty,
    onSave: onSaveButtonClick,
    onDiscard: onDiscardButtonClick,
  };

  const ingredientSaveDiscardButtons = useSaveDiscardButtons(saveDiscardButtonsParams);

  return ingredientSaveDiscardButtons;
};

export const useGetStocksTableData = (props: UseGetStocksTableDataProps) => {
  const { onCellChanged, vendors, liquidClasses, ingInfo, concUnits } = props;
  const stocks = useIngredientStocks();
  const hasBufferType = useHasBufferType();
  const hasNonBufferType = useHasNonBufferType();

  const stockRows = useMemo(
    () =>
      stocks.map((s) =>
        StockRowRenderer({
          stock: s,
          ingInfo,
          vendors,
          liquidClasses,
          hasBufferType,
          onCellChanged,
          concUnits,
          hasNonBufferType,
        }),
      ),
    [stocks, onCellChanged, ingInfo],
  );
  return stockRows;
};

export const useGetAllVendors = () => {
  const [vendors, setVendors] = useState([]);
  useEffect(() => {
    getVendors().then((vendors) => {
      setVendors([{ id: -1, name: '--Select--' }, ...vendors]);
    });
  }, []);

  return vendors;
};

export const useGetAllLiquidClasses = () => {
  const [liquidClasses, setLiquidClasses] = useState([]);
  const [defaultLiquidClassId, setDefaultLiquidClassId] = useState<number>();
  const globalPrefService = new GlobalPreferencesService();

  useEffect(() => {
    getLiquidClasses().then((liquidClasses) => {
      setLiquidClasses(liquidClasses);
      globalPrefService
        .getProperty(PropertyConstants.StocksDefaultLiquidClass)
        .then((defaultClass) => {
          const defaultClassName = (defaultClass as StringPropertyDTO)?.value;
          const defaultLiquidClass = liquidClasses.find(
            (lc) => lc.name.toLocaleLowerCase() === defaultClassName.toLocaleLowerCase(),
          );
          setDefaultLiquidClassId(
            defaultLiquidClass ? defaultLiquidClass.id : liquidClasses[0]?.id,
          );
        });
    });
  }, []);

  return { liquidClasses, defaultLiquidClassId };
};

export const useFetchConcentrationUnits = () => {
  const [concUnits, setConcUnits] = useState([]);

  useEffect(() => {
    const FetchConcentrationUnits = async () => {
      const units: StringPropertyDTO = await new GlobalPreferencesService().getProperty(
        PropertyConstants.ConcentrationUnits,
      );
      const unitsOptions =
        units?.value?.split(',')?.map((u) => {
          return { title: u.trim(), value: u.trim() } as IOption;
        }) || [];
      unitsOptions.unshift({ title: '', value: '' });
      setConcUnits(unitsOptions);
    };
    FetchConcentrationUnits();
  }, []);

  return concUnits;
};

export const useGetStockDefaultDispenseOrder = () => {
  const [dispenseOrder, setDispenseOrder] = useState<number>(1);
  const globalPrefService = new GlobalPreferencesService();
  useEffect(() => {
    globalPrefService
      .getProperty(PropertyConstants.StocksDefaultDispenseOrder)
      .then((prop) => {
        const order = (prop as IntPropertyDTO)?.value;
        if (order) {
          setDispenseOrder(order);
        }
      });
  }, []);
  return dispenseOrder;
};
